import React from "react";
import { useTranslation } from "react-i18next";
import BaseInput from "@reusables/BaseInput";
import { Search } from "lucide-react";
import BaseMaterialIconButton from "@reusables/BaseMaterialIconButton";

type BaseSearchInputProps = {
    onSearch: (search: string) => void;
    /**
     * Keeps the input expanded instead of collapsing it into a magnifier button.
     */
    permanent?: boolean;
    placeholder?: string;
};

/**
 * Compact search field, which collapses into a magnifier button when empty and unfocused.
 * Pass `permanent` to always keep it expanded.
 */
export default function BaseSearchInput({
    onSearch,
    permanent,
    placeholder,
}: BaseSearchInputProps) {
    const { t } = useTranslation();

    const [isShowSearch, setIsShowSearch] = React.useState(false);
    const [search, setSearch] = React.useState("");

    if (isShowSearch || permanent || search) {
        return (
            <BaseInput
                className={"animate-in slide-in-from-right-0.5"}
                type="text"
                placeholder={placeholder ?? t("general.search.placeholder")}
                value={search}
                onBlur={() => {
                    if (!search.length) setIsShowSearch(false);
                }}
                onChange={(val) => {
                    setSearch(val ?? "");
                    onSearch(val ?? "");
                }}
                // Only grab focus when the user actively revealed the field via the magnifier.
                // A `permanent` field is part of the layout, so stealing focus would scroll the page on mount.
                autoFocus={isShowSearch}
                sx={{
                    "& .MuiInput-input": {
                        padding: "7.5px",
                    },
                }}
                icon={{
                    right: {
                        el: <Search size={16} style={{ marginTop: "-3px" }} />,
                        offset: "12px",
                    },
                }}
            />
        );
    }

    return (
        <BaseMaterialIconButton icon={<Search size={16} />} onClick={() => setIsShowSearch(true)} />
    );
}
